import { faExclamationCircle, faWarning } from '@fortawesome/pro-solid-svg-icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Modal, NotificationDispatch, TextField } from '@tryhackme/thm-ui-components';
import { useForm } from 'src/common/form';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import type { DeleteUserData } from 'src/common/interfaces';
import { useDeleteUserMutation } from 'src/features/admin/manage-users/manage-users.slice';
import { StyledPasswordInputWrapper } from 'src/features/admin/manage-users/modals/delete-user/delete-user-modal.styles';
import { DEFAULT_INPUT_VALUES, deleteUserFormValidation } from 'src/features/admin/manage-users/modals/delete-user/delete-user.constants';
import { StyledActionButtonsWrapper, StyledContent, StyledErrorToast, StyledFontAwesomeErrorIcon, StyledFontAwesomeWarningIcon, StyledInput, StyledText, StyledTitle, StyledWarningToast, StyledWrapper } from 'src/features/admin/manage-users/modals/modals.styles';
import type { ModalProps } from '../modal.types';
export const ModalDeleteUser = ({
  isOpen,
  setIsOpen,
  userId,
  triggerElement
}: ModalProps) => {
  const [deleteUserData] = useDeleteUserMutation();
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: {
      isValid,
      isLoading
    }
  } = useForm({
    resolver: yupResolver(deleteUserFormValidation),
    mode: 'onTouched',
    defaultValues: DEFAULT_INPUT_VALUES
  });
  const closeModal = () => {
    setIsOpen(!isOpen);
    reset();
  };
  const onSubmit = async () => {
    const {
      password
    } = getValues();
    try {
      await deleteUserData({
        userId,
        password
      } as DeleteUserData).unwrap();
      NotificationDispatch('success', 'User successfully deleted');
      closeModal();
      reset();
      window.location.reload();
    } catch (error: unknown) {
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error.data.message : 'An unknown error has occurred');
    }
  };
  return <Modal open={isOpen} onOpenChange={closeModal} isSmall triggerElement={triggerElement} data-sentry-element="Modal" data-sentry-component="ModalDeleteUser" data-sentry-source-file="delete-user-modal.tsx">
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <StyledWrapper data-sentry-element="StyledWrapper" data-sentry-source-file="delete-user-modal.tsx">
          <StyledTitle data-sentry-element="StyledTitle" data-sentry-source-file="delete-user-modal.tsx">
            <StyledFontAwesomeErrorIcon icon={faExclamationCircle} data-sentry-element="StyledFontAwesomeErrorIcon" data-sentry-source-file="delete-user-modal.tsx" />
            Delete account
          </StyledTitle>
          <StyledContent data-sentry-element="StyledContent" data-sentry-source-file="delete-user-modal.tsx">
            <StyledWarningToast data-sentry-element="StyledWarningToast" data-sentry-source-file="delete-user-modal.tsx">
              <StyledFontAwesomeWarningIcon icon={faWarning} data-sentry-element="StyledFontAwesomeWarningIcon" data-sentry-source-file="delete-user-modal.tsx" />
              When deleting a user account, all account details will be filled with dummy data and the user will
              permanently lose access to their account.
            </StyledWarningToast>
            <StyledErrorToast data-sentry-element="StyledErrorToast" data-sentry-source-file="delete-user-modal.tsx">
              <StyledFontAwesomeErrorIcon icon={faExclamationCircle} data-sentry-element="StyledFontAwesomeErrorIcon" data-sentry-source-file="delete-user-modal.tsx" />
              Action is irreversible! to continue, type DELETE ACCOUNT in the box below.
            </StyledErrorToast>
            <TextField {...register('validation')} id="delete-user-modal-validation" variant="input" placeholder="Type DELETE ACCOUNT here" data-sentry-element="TextField" data-sentry-source-file="delete-user-modal.tsx" />
            <StyledPasswordInputWrapper data-sentry-element="StyledPasswordInputWrapper" data-sentry-source-file="delete-user-modal.tsx">
              <StyledText data-sentry-element="StyledText" data-sentry-source-file="delete-user-modal.tsx">Type your password below in order to delete the user account</StyledText>
              <StyledInput data-sentry-element="StyledInput" data-sentry-source-file="delete-user-modal.tsx">
                <StyledText data-sentry-element="StyledText" data-sentry-source-file="delete-user-modal.tsx">Password</StyledText>
                <TextField {...register('password')} id="delete-user-modal-password" variant="input" placeholder="Type your password here" data-sentry-element="TextField" data-sentry-source-file="delete-user-modal.tsx" />
              </StyledInput>
            </StyledPasswordInputWrapper>
          </StyledContent>
          <StyledActionButtonsWrapper data-sentry-element="StyledActionButtonsWrapper" data-sentry-source-file="delete-user-modal.tsx">
            <Button onClick={closeModal} variant="outlined" color="secondary" data-sentry-element="Button" data-sentry-source-file="delete-user-modal.tsx">
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || isLoading} isLoading={isLoading} variant="solid" color="error" data-sentry-element="Button" data-sentry-source-file="delete-user-modal.tsx">
              Delete forever
            </Button>
          </StyledActionButtonsWrapper>
        </StyledWrapper>
      </form>
    </Modal>;
};